import * as THREE from "three";

/**
 * Procedural canvas textures for doors (W17).
 *
 * These fill the gap until authored PNGs ship under
 * `public/<adventure>/world/doors/{closed,open}.png`. Both
 * textures live on a transparent background so the open state reads as
 * an archway with a visible frame that is still clickable.
 */

const TEX_SIZE = 128;

let cachedClosed: THREE.CanvasTexture | null = null;
let cachedOpen: THREE.CanvasTexture | null = null;

export function getClosedDoorTexture(): THREE.CanvasTexture {
  if (cachedClosed) return cachedClosed;
  cachedClosed = drawClosedDoor();
  return cachedClosed;
}

export function getOpenDoorTexture(): THREE.CanvasTexture {
  if (cachedOpen) return cachedOpen;
  cachedOpen = drawOpenDoor();
  return cachedOpen;
}

function drawClosedDoor(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = TEX_SIZE;
  canvas.height = TEX_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("doorTextures: 2D context unavailable");

  // Transparent outer margin frames the door inside its cell.
  ctx.clearRect(0, 0, TEX_SIZE, TEX_SIZE);

  const pad = 10;
  const w = TEX_SIZE - pad * 2;
  const h = TEX_SIZE - pad * 2;

  // Frame
  ctx.fillStyle = "#2a1a0e";
  ctx.fillRect(pad, pad, w, h);

  // Door panel
  ctx.fillStyle = "#5a3820";
  ctx.fillRect(pad + 4, pad + 4, w - 8, h - 8);

  // Plank seams
  ctx.strokeStyle = "#2a1a0e";
  ctx.lineWidth = 2;
  const planks = 3;
  for (let i = 1; i < planks; i++) {
    const x = pad + 4 + ((w - 8) * i) / planks;
    ctx.beginPath();
    ctx.moveTo(x, pad + 6);
    ctx.lineTo(x, TEX_SIZE - pad - 6);
    ctx.stroke();
  }

  // Hinges
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(pad + 6, pad + 12, 6, 8);
  ctx.fillRect(pad + 6, TEX_SIZE - pad - 20, 6, 8);

  // Doorknob
  ctx.beginPath();
  ctx.fillStyle = "#d4a017";
  ctx.arc(TEX_SIZE - pad - 14, TEX_SIZE / 2, 4, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  // Canvas pixels are sRGB — tag them or the renderer gamma-lifts on output.
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  return tex;
}

function drawOpenDoor(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = TEX_SIZE;
  canvas.height = TEX_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("doorTextures: 2D context unavailable");

  ctx.clearRect(0, 0, TEX_SIZE, TEX_SIZE);

  const pad = 10;
  const w = TEX_SIZE - pad * 2;
  const h = TEX_SIZE - pad * 2;

  // Just a thin frame — the middle stays transparent so the player
  // sees through the doorway. The mesh itself remains clickable,
  // which is what the toggle UX relies on to let the player close it.
  ctx.fillStyle = "#2a1a0e";
  ctx.fillRect(pad, pad, w, 4);
  ctx.fillRect(pad, TEX_SIZE - pad - 4, w, 4);
  ctx.fillRect(pad, pad, 4, h);
  ctx.fillRect(TEX_SIZE - pad - 4, pad, 4, h);

  const tex = new THREE.CanvasTexture(canvas);
  // Canvas pixels are sRGB — tag them or the renderer gamma-lifts on output.
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  return tex;
}
